import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import { getAccountId } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useNtbMetricsGate } from '~/modules/retailMedia/hooks/useNtbMetricsEnabled'
import { listAdvertiserService } from '~/modules/retailMedia/services/advertiser'
import { useAppSelector } from '~/store/hooks'
import { filterRoutes } from '~/store/modules/accessModules/helpers/filterRoutes'

import LanguageToggle from '../LanguageToggle'
import LogoMenu from '../LogoMenu'
import ToogleInput from '../Toggle'
import Children from './Children'
import { checkRules } from './helpers'
import SidebarItem from './SidebarItem'

const Sidebar: React.FC = () => {
  const { isWhiteLabel, allowSuperAdmin, superAdmin, toggleSuperAdmin } =
    useAppInfo()

  const {
    sidebar,
    signin: { accountType },
    accessModules: { modules }
  } = useAppSelector(state => state)

  const accountId = getAccountId()
  const { isEnabled: isNtbEnabled } = useNtbMetricsGate()

  const { data: advertiserData } = useQuery({
    queryKey: ['sidebar-advertiser-info', accountId],
    enabled: !!superAdmin && !!accountId,
    staleTime: Infinity,
    queryFn: async () => {
      const res = await listAdvertiserService({
        account_id: accountId,
        count: false
      })
      return res.data?.[0]
    }
  })

  const menu = useMemo(() => {
    const campaignManagerHref = advertiserData?.alternative_advertiser_id
      ? `https://${advertiserData.alternative_advertiser_id}.myvtex.com/admin/campaign-manager`
      : undefined

    const routes = filterRoutes({
      modules,
      accountType,
      filterRoute: route => !route.onlyNtbEnabled || isNtbEnabled
    })

    return routes.map(route => ({
      ...route,
      children: route.children?.map(child => {
        if (child.path !== '/retail-media/tools/campaign-manager') return child
        return {
          ...child,
          href: campaignManagerHref,
          hideMenu: !campaignManagerHref
        }
      })
    }))
  }, [accountType, modules, advertiserData, isNtbEnabled])

  const location = useLocation()
  const pathName = location.pathname

  return (
    <nav
      className={`sidebar${!sidebar.isOpen ? ' toggled' : ''}${
        sidebar.isSticky ? ' sidebar-sticky' : ''
      }`}
    >
      <div className="sidebar-content">
        <LogoMenu />

        <ul className="sidebar-nav">
          {menu.map((route, index) => {
            const showItem = route?.onlySuperAdmin ? !!superAdmin : true
            const hasVisibleChildren = !!route?.children?.filter(
              item => !item?.hideMenu
            )?.length
            // Só esconde o pai quando filterRoutes zerou os filhos (ex.: onlyAdvertiser).
            // Rotas como /users mantêm filhos com hideMenu só para o React Router —
            // o item raiz ainda precisa aparecer no menu.
            const isEmptyGroup = route?.children?.length === 0

            let itemContent: React.ReactNode = null
            if (hasVisibleChildren) {
              itemContent = <Children route={route} pathName={pathName} />
            } else if (showItem && !isEmptyGroup) {
              itemContent = (
                <SidebarItem
                  name={route.name}
                  to={route.path?.replace('/*', '')}
                  childPaths={route?.childPaths}
                  icon={route.icon}
                  badgeColor={route.badgeColor}
                  badgeText={route.badgeText}
                />
              )
            }
            return (
              <React.Fragment key={`item-${index}`}>
                {checkRules(route) && (
                  <>
                    {route.header ? (
                      <li className="sidebar-header">{route.header}</li>
                    ) : null}

                    {itemContent}
                  </>
                )}
              </React.Fragment>
            )
          })}
        </ul>
      </div>

      <footer>
        &copy; {new Date().getFullYear()} -{' '}
        <small>v{String(process.env.VERSION)}</small> -{' '}
        {isWhiteLabel ? 'by ' : ''}
        <a href="https://ads.vtex.com" target="_blank" rel="noreferrer">
          VTEX
        </a>
      </footer>

      <footer>
        <LanguageToggle />

        {allowSuperAdmin && (
          <div className="d-flex justify-content-center py-2 border-top border-bottom flex-wrap">
            <ToogleInput
              size="small"
              isChecked={superAdmin}
              onChange={toggleSuperAdmin}
              name="superAdmin"
              label="Ativar super admin"
            />
          </div>
        )}
      </footer>
    </nav>
  )
}

export default Sidebar
